import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Accordion = styled.details`
  margin: 0 0 1.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 2px;
`;

const Summary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  list-style: none;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.foreground};
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  ${Accordion}[open] & svg {
    transform: rotate(180deg);
  }
`;

const SummaryIcon = styled(ChevronDown)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.mutedForeground};
  transition: transform 0.2s ease;
`;

const Panel = styled.div`
  padding: 0 1rem 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Grid = styled.nav`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.45rem 1rem;
  padding-top: 0.85rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TocLink = styled.a`
  display: block;
  font-size: ${({ $level, theme }) => ($level === 3 ? theme.type.bodyXs : theme.type.bodySm)};
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-decoration: none;
  padding: 0.2rem 0;
  padding-left: ${({ $level }) => ($level === 3 ? '0.65rem' : '0')};
  border-left: ${({ $level, theme }) =>
    $level === 3 ? `2px solid ${theme.colors.border}` : 'none'};
  transition: color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

function scrollToHeading(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * @param {{ entries: { id: string, text: string, level: number }[] }} props
 */
export default function JournalTableOfContents({ entries = [] }) {
  const { t } = useTranslation();
  const p = 'modamai.blogPost';

  if (!entries.length) return null;

  return (
    <Accordion>
      <Summary>
        {t(`${p}.tocTitle`, { count: entries.length })}
        <SummaryIcon size={18} aria-hidden />
      </Summary>
      <Panel>
        <Grid aria-label={t(`${p}.tocAria`)}>
          {entries.map((item) => (
            <TocLink
              key={item.id}
              href={`#${item.id}`}
              $level={item.level}
              onClick={(e) => {
                e.preventDefault();
                scrollToHeading(item.id);
              }}
            >
              {item.text}
            </TocLink>
          ))}
        </Grid>
      </Panel>
    </Accordion>
  );
}
